import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type PointerEvent as ReactPointerEvent, type ReactElement, type ReactNode } from 'react';
import { Camera, Expand, Play, SlidersHorizontal, X } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Button,
  Calendar,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  ToggleGroup,
  ToggleGroupItem,
  VibesitesImage,
  cn,
} from '@vibesites/ui-primitives';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, MenuIcon, TableCellsLargeIcon } from '@vibesites/assets';
import type { Fee, SpecialOffer } from '@vibesites/schema';
import type {
  AmenityGroup,
  AmenityLine,
  BedChoice,
  BedroomFilter,
  Contact,
  FeeList,
  FeeSource,
  ListedFee,
  FloorplansLayout,
  FloorplansViewProps,
  IsoDate,
  LayoutSwitch,
  ListedUnit,
  PlanFilters,
  PlanPhoto,
  PlanSort,
  PlanSorter,
  Range,
  Refinement,
  Tour,
  UnitColumns,
} from './model';
import { NO_FILTERS, amenityGroups, feesFor, sharedRequiredFees } from './model';
import { formatDay, formatDollars, formatFeeAmount, formatNumber, formatRange, formatSpecial, isoDate, text, tourEmbed, type TourEmbed } from './wire';

type ReadyContent = Extract<FloorplansViewProps['content'], { status: 'ready' }>;
type ReadyPlan = ReadyContent['plans'][number];

/** Thumb, plan, availability, rent, action. Rows are tablet and up; phones always get cards. */
const ROW_GRID = 'grid grid-cols-[5rem_minmax(0,1fr)_11rem_10rem_10rem] items-center gap-x-md';
const EYEBROW = 'text-xs font-medium uppercase tracking-wider text-muted-foreground';
const UNITS_PREVIEW = 5;
/**
 * Neutral panels and hovers, mixed from the section's own text color so they contrast on any background.
 * Selected and highlighted states use primary or secondary instead. `bg-muted` is the theme surface,
 * which themes set unreliably. Tailwind 3 drops `/N` opacity on var() colors, so tints use color-mix.
 */
const TINT = 'bg-[color-mix(in_srgb,var(--vibe-color-text)_6%,transparent)]';
const TINT_HOVER = 'hover:bg-[color-mix(in_srgb,var(--vibe-color-text)_10%,transparent)]';
/** "Show all" and "Show fewer". Text color, not a brand color, which some themes set too light to read as text. */
const MORE = 'mt-sm text-sm font-medium text-foreground underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
/** Button's link variant at text size, in text color. A brand color can be too light to read as text. */
const INLINE = 'h-auto whitespace-normal p-0 text-left text-foreground [overflow-wrap:anywhere] underline underline-offset-4 hover:no-underline';
/** One card per row on phones, two from md, three from xl inside the max-w-6xl container. */
const CARD_SIZES = '(min-width: 1280px) 384px, (min-width: 768px) 50vw, 100vw';

/** Button's outline variant in text color, hovered with the text tint. Its own hover is the accent color, which themes set unreliably. */
const OUTLINE = cn('text-foreground hover:text-foreground', TINT_HOVER);

/** `target` and `rel` for a link set to open in a new tab. */
function tabProps(newTab: boolean): { target?: '_blank'; rel?: string } {
  return newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {};
}

export function DefaultFloorplansView({ header, layout, content }: FloorplansViewProps): ReactElement {
  return (
    <section className="bg-background font-body text-foreground">
      <div className="mx-auto max-w-6xl px-md py-xl">
        {(header || content.status === 'ready' || content.status === 'loading') && (
          <div className="mb-lg flex flex-col gap-md md:flex-row md:items-end md:justify-between">
            {header && (
              <div>
                <header.Title as="h2" className="font-heading text-4xl text-foreground md:text-5xl" />
                {header.Subtitle && <header.Subtitle as="p" className="mt-sm text-muted-foreground" />}
              </div>
            )}
            {content.status === 'ready' && <Summary plans={content.plans} />}
            {content.status === 'loading' && <SummarySkeleton />}
          </div>
        )}
        {content.status === 'ready' && <Toolbar content={content} layout={layout} />}
        {content.status === 'loading' && <LoadingToolbar sort={content.sort} layout={layout} />}
        <Body content={content} layout={layout.selected} />
      </div>
    </section>
  );
}

function Summary({ plans }: { plans: readonly ReadyPlan[] }): ReactElement | null {
  const open = plans.filter(isAvailable);
  const homes = open.reduce((total, plan) => total + plan.listed.length, 0);
  const specials = open.reduce((total, plan) => total + plan.homesWithSpecials, 0);
  if (!homes) return null;
  return (
    <p className="text-sm text-muted-foreground md:text-right">
      <span className="font-semibold text-foreground">{`${homes} ${homes === 1 ? 'home' : 'homes'}`}</span>
      {` available across ${open.length} ${open.length === 1 ? 'plan' : 'plans'}`}
      {specials > 0 && <span className="block">{`Specials on ${specials} ${specials === 1 ? 'home' : 'homes'}`}</span>}
    </p>
  );
}

function SummarySkeleton(): ReactElement {
  return (
    <div aria-hidden className="flex flex-col md:items-end">
      <TextBone text="text-sm" className="h-3 w-56" />
      <TextBone text="text-sm" className="h-3 w-32" />
    </div>
  );
}

const TOOLBAR = 'mb-xl flex flex-col gap-sm md:flex-row md:items-center md:gap-md';

function Toolbar({ content, layout }: { content: ReadyContent; layout: LayoutSwitch }): ReactElement {
  const { filter, sort, refine } = content;
  return (
    <>
      <div className={TOOLBAR}>
        {filter && <BedroomSegments filter={filter} />}
        <div className="flex items-center gap-sm md:ml-auto">
          {sort && <SortSelect sort={sort} />}
          {refine && <FiltersPopover content={content} refine={refine} />}
          <div className="hidden md:block">
            <LayoutToggle layout={layout} />
          </div>
        </div>
      </div>
      {refine && refine.active > 0 && <AppliedFilters content={content} refine={refine} />}
    </>
  );
}

/** Sort and layout work before the plans arrive. The bedroom choices come from the plans, and the filter ranges too, so those wait. */
function LoadingToolbar({ sort, layout }: { sort: PlanSorter | undefined; layout: LayoutSwitch }): ReactElement {
  return (
    <div className={TOOLBAR}>
      <div aria-hidden className="flex w-full gap-xs rounded-lg border border-border p-xs md:w-auto">
        {['md:w-9', 'md:w-14', 'md:w-16', 'md:w-16'].map((width, key) => (
          <Bone key={key} className={cn('h-8 flex-1 md:flex-none', width)} />
        ))}
      </div>
      <div className="flex items-center gap-sm md:ml-auto">
        {sort && <SortSelect sort={sort} />}
        <Button type="button" variant="outline" disabled className={cn('h-10 gap-sm', OUTLINE)}>
          <SlidersHorizontal aria-hidden size={16} />
          Filters
        </Button>
        <div className="hidden md:block">
          <LayoutToggle layout={layout} />
        </div>
      </div>
    </div>
  );
}

function bedValue(beds: BedChoice): string {
  return beds == null ? 'all' : String(beds);
}

function BedroomSegments({ filter }: { filter: BedroomFilter }): ReactElement {
  return (
    <ToggleGroup
      type="single"
      value={bedValue(filter.selected)}
      onValueChange={(value) => {
        if (!value) return;
        filter.select(value === 'all' ? null : Number(value));
      }}
      aria-label="Bedrooms"
      className="flex w-full gap-xs overflow-x-auto rounded-lg border border-border p-xs [scrollbar-width:none] md:w-auto [&>button]:flex-1 md:[&>button]:flex-none"
    >
      {filter.options.map((option) => (
        <ToggleGroupItem key={option.label} value={bedValue(option.beds)} className={cn('h-8 whitespace-nowrap px-sm text-foreground', TINT_HOVER)}>
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

function SortSelect({ sort }: { sort: PlanSorter }): ReactElement {
  return (
    <Select value={sort.selected} onValueChange={(value) => sort.select(value as PlanSort)}>
      <SelectTrigger aria-label="Sort floor plans" className={cn('w-auto min-w-0 flex-1 gap-sm border-transparent bg-transparent md:flex-none', TINT_HOVER)}>
        <span className="text-muted-foreground">Sort</span>
        <span className="font-medium text-foreground">
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent align="end">
        {sort.options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className="text-foreground focus:bg-primary focus:text-primary-foreground data-[state=checked]:font-semibold"
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Rent, size, and move-in. Every change applies at once, so the footer shows the live count instead of a button. */
function FiltersPopover({ content, refine }: { content: ReadyContent; refine: Refinement }): ReactElement {
  const set = (patch: Partial<PlanFilters>) => refine.select({ ...refine.selected, ...patch });
  const { today } = content;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className={cn('h-10 gap-sm', OUTLINE)}>
          <SlidersHorizontal aria-hidden size={16} />
          Filters
          {refine.active > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-xs text-xs font-semibold text-primary-foreground">
              {refine.active}
              <span className="sr-only"> set</span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex max-h-[var(--radix-popover-content-available-height)] w-[25rem] flex-col p-0">
        <div className="min-h-0 flex-1 overflow-y-auto [&>section+section]:border-t [&>section+section]:border-border">
          {refine.rent && (
            <PanelSection title="Monthly rent" value={rangeText(refine.selected.rent, refine.rent, formatDollars)}>
              <RangeField label="rent" bounds={refine.rent} value={refine.selected.rent} format={formatDollars} onChange={(rent) => set({ rent })} />
            </PanelSection>
          )}
          {refine.sqft && (
            <PanelSection title="Square feet" value={rangeText(refine.selected.sqft, refine.sqft, formatNumber, ' sq ft')}>
              <RangeField label="size" bounds={refine.sqft} value={refine.selected.sqft} format={formatNumber} onChange={(sqft) => set({ sqft })} />
            </PanelSection>
          )}
          {refine.baths && (
            <PanelSection title="Bathrooms">
              <ChoiceField
                label="Bathrooms"
                options={refine.baths.map((baths) => ({ value: String(baths), label: formatNumber(baths) }))}
                value={refine.selected.baths == null ? null : String(refine.selected.baths)}
                onChange={(baths) => set({ baths: baths == null ? null : Number(baths) })}
              />
            </PanelSection>
          )}
          {refine.floors && (
            <PanelSection title="Floor">
              <ChoiceField
                label="Floor"
                options={refine.floors.map((floor) => ({ value: floor, label: floor }))}
                value={refine.selected.floor}
                onChange={(floor) => set({ floor })}
              />
            </PanelSection>
          )}
          {refine.moveIn && (
            <PanelSection title="Move in by" value={refine.selected.moveInBy ? formatDay(refine.selected.moveInBy, today) : 'Any date'}>
              <MoveInField today={today} latest={refine.moveIn.latest} value={refine.selected.moveInBy} onChange={(moveInBy) => set({ moveInBy })} />
            </PanelSection>
          )}
        </div>
        <div className="flex items-center justify-between gap-md border-t border-border px-lg py-md">
          <Button type="button" variant="link" className={cn(INLINE, 'text-sm')} disabled={!refine.active} onClick={() => refine.select(NO_FILTERS)}>
            Clear all
          </Button>
          <MatchCount plans={content.plans} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PanelSection({ title, value, children }: { title: string; value?: string; children: ReactNode }): ReactElement {
  return (
    <section className="p-lg">
      <div className="mb-md flex items-baseline justify-between gap-md">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {value && <span className="text-sm text-muted-foreground">{value}</span>}
      </div>
      {children}
    </section>
  );
}

/**
 * One pick, with Any first. Values are the feed's own, so a floor reads exactly as sent. Up to CHOICE_CHIPS options
 * show as chips. A longer list, like a tower's floors, is a dropdown so it does not crowd the panel.
 */
function ChoiceField({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { readonly value: string; readonly label: string }[];
  value: string | null;
  onChange: (value: string | null) => void;
}): ReactElement {
  if (options.length > CHOICE_CHIPS) {
    return (
      <Select value={value ?? ANY} onValueChange={(next) => onChange(next === ANY ? null : next)}>
        <SelectTrigger aria-label={label} className={cn('w-full text-foreground', OUTLINE)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {[{ value: ANY, label: 'Any' }, ...options].map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className="text-foreground focus:bg-primary focus:text-primary-foreground data-[state=checked]:font-semibold"
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <ToggleGroup
      type="single"
      value={value ?? ANY}
      onValueChange={(next) => next && onChange(next === ANY ? null : next)}
      aria-label={label}
      className="flex flex-wrap justify-start gap-xs"
    >
      {[{ value: ANY, label: 'Any' }, ...options].map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          className={cn('h-8 min-w-10 rounded-md border border-border px-sm text-foreground data-[state=on]:border-primary', TINT_HOVER)}
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

const CHOICE_CHIPS = 8;

/** ChoiceField's value for Any. Not a bath count or a floor any feed sends. */
const ANY = '__any__';

/** "42 homes in 3 plans", counting only plans with homes like the summary does. Else the plan count. */
function MatchCount({ plans }: { plans: readonly ReadyPlan[] }): ReactElement {
  const homes = plans.reduce((total, plan) => total + plan.listed.length, 0);
  const listing = plans.filter((plan) => plan.listed.length).length;
  return (
    <p className="flex items-center gap-xs text-sm text-muted-foreground" aria-live="polite">
      {!plans.length ? (
        'No matches'
      ) : homes ? (
        <>
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-success" />
          <span className="font-semibold text-foreground">{`${homes} ${homes === 1 ? 'home' : 'homes'}`}</span>
          {`in ${listing} ${listing === 1 ? 'plan' : 'plans'}`}
        </>
      ) : (
        `${plans.length} ${plans.length === 1 ? 'plan' : 'plans'} match`
      )}
    </p>
  );
}

/** One pill per filter in use, each with its own remove button, so a visitor can undo one without reopening the popover. */
function AppliedFilters({ content, refine }: { content: ReadyContent; refine: Refinement }): ReactElement {
  const { selected } = refine;
  const pills: { key: keyof PlanFilters; label: string }[] = [];
  if (selected.rent && refine.rent) pills.push({ key: 'rent', label: rangeText(selected.rent, refine.rent, formatDollars) });
  if (selected.sqft && refine.sqft) pills.push({ key: 'sqft', label: rangeText(selected.sqft, refine.sqft, formatNumber, ' sq ft') });
  if (selected.baths != null) pills.push({ key: 'baths', label: `${formatNumber(selected.baths)} ${selected.baths === 1 ? 'bath' : 'baths'}` });
  if (selected.floor != null) pills.push({ key: 'floor', label: `Floor: ${selected.floor}` });
  if (selected.moveInBy) pills.push({ key: 'moveInBy', label: selected.moveInBy === content.today ? 'Move in now' : `Move in by ${formatDay(selected.moveInBy, content.today)}` });
  return (
    <div className="-mt-sm mb-lg flex flex-wrap items-center gap-sm animate-in fade-in-0 motion-reduce:animate-none">
      <MatchCount plans={content.plans} />
      <span aria-hidden className="h-4 w-px bg-border" />
      {pills.map((pill) => (
        <span key={pill.key} className="inline-flex h-7 items-center gap-xs rounded-full border border-border pl-sm pr-xs text-sm text-foreground">
          {pill.label}
          <button
            type="button"
            aria-label={`Remove ${pill.label}`}
            onClick={() => refine.select({ ...selected, [pill.key]: null })}
            className={cn('inline-flex h-5 w-5 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', TINT_HOVER)}
          >
            <X aria-hidden size={12} />
          </button>
        </span>
      ))}
      <Button type="button" variant="link" className={cn(INLINE, 'text-sm')} onClick={() => refine.select(NO_FILTERS)}>
        Clear all
      </Button>
    </div>
  );
}

/** "Any", "Up to $1,800", "$1,200+", or "$1,200 to $1,800" against the slider's ends. */
function rangeText(value: Range | null, bounds: Range, format: (value: number) => string, suffix = ''): string {
  if (!value) return suffix ? 'Any size' : 'Any';
  if (value.min <= bounds.min) return `Up to ${format(value.max)}${suffix}`;
  if (value.max >= bounds.max) return `${format(value.min)}+${suffix}`;
  return `${format(value.min)} to ${format(value.max)}${suffix}`;
}

/** Controlled by the filter itself, so the list follows the thumbs and Clear all resets them. */
function RangeField({
  label,
  bounds,
  value,
  format,
  onChange,
}: {
  label: string;
  bounds: Range & { step: number };
  value: Range | null;
  format: (value: number) => string;
  onChange: (value: Range | null) => void;
}): ReactElement {
  return (
    <>
      <Slider
        min={bounds.min}
        max={bounds.max}
        step={bounds.step}
        minStepsBetweenThumbs={1}
        value={[value?.min ?? bounds.min, value?.max ?? bounds.max]}
        onValueChange={([min, max]) => onChange(min <= bounds.min && max >= bounds.max ? null : { min, max })}
        thumbLabels={[`Lowest ${label}`, `Highest ${label}`]}
      />
      <div className="mt-sm flex justify-between text-xs tabular-nums text-muted-foreground">
        <span>{format(bounds.min)}</span>
        <span>{format(bounds.max)}</span>
      </div>
    </>
  );
}

/** The calendar runs from today to the latest listed date. "Move in now" and "Any date" cover the common answers. */
function MoveInField({
  today,
  latest,
  value,
  onChange,
}: {
  today: IsoDate;
  latest: IsoDate;
  value: IsoDate | null;
  onChange: (value: IsoDate | null) => void;
}): ReactElement {
  return (
    <>
      <Calendar
        mode="single"
        selected={value ? dayOf(value) : undefined}
        defaultMonth={dayOf(value ?? today)}
        startMonth={dayOf(today)}
        endMonth={dayOf(latest)}
        disabled={{ before: dayOf(today), after: dayOf(latest) }}
        onSelect={(day) => onChange(day ? isoOfDay(day) : null)}
        className={CALENDAR}
        classNames={{ root: 'w-full', today: 'rounded-md font-semibold underline underline-offset-4' }}
      />
      <div className="mt-sm flex gap-md border-t border-border pt-sm">
        <Button type="button" variant="link" className={cn(INLINE, 'text-sm', value === today && 'font-semibold')} onClick={() => onChange(today)}>
          Move in now
        </Button>
        <Button type="button" variant="link" className={cn(INLINE, 'text-sm', value == null && 'font-semibold')} onClick={() => onChange(null)}>
          Any date
        </Button>
      </div>
    </>
  );
}

/**
 * The panel already paints the background. Columns stretch to the panel's width and rows stay 40px tall.
 * Hover uses the text tint and today an underline, since the calendar's accent fill is a color themes set
 * unreliably. The selected day keeps primary.
 */
const CALENDAR = cn(
  'bg-transparent p-0',
  '[&_.rdp-day]:aspect-auto [&_.rdp-day_button]:aspect-auto [&_.rdp-day_button]:h-10',
  '[&_button:not([data-selected-single=true]):hover]:bg-[color-mix(in_srgb,var(--vibe-color-text)_10%,transparent)]',
  '[&_button:not([data-selected-single=true]):hover]:text-foreground',
);

/** Local midnight on an IsoDate, for the calendar. */
function dayOf(iso: IsoDate): Date {
  return new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
}

function isoOfDay(day: Date): IsoDate | null {
  return isoDate(`${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`);
}

const LAYOUT_OPTIONS: readonly { value: FloorplansLayout; label: string; Icon: typeof MenuIcon }[] = [
  { value: 'rows', label: 'Table', Icon: MenuIcon },
  { value: 'cards', label: 'Cards', Icon: TableCellsLargeIcon },
];

function LayoutToggle({ layout }: { layout: LayoutSwitch }): ReactElement {
  return (
    <ToggleGroup
      type="single"
      value={layout.selected}
      onValueChange={(value) => {
        if (value === 'rows' || value === 'cards') layout.select(value);
      }}
      aria-label="Layout"
      className="flex gap-xs rounded-lg border border-border p-xs"
    >
      {LAYOUT_OPTIONS.map(({ value, label, Icon }) => (
        <ToggleGroupItem key={value} value={value} aria-label={label} title={label} className={cn('h-8 shrink-0 px-sm text-foreground', TINT_HOVER)}>
          <Icon aria-hidden size={16} />
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

function Body({ content, layout }: { content: FloorplansViewProps['content']; layout: FloorplansLayout }): ReactElement {
  switch (content.status) {
    case 'loading':
      return (
        <>
          <p role="status" className="sr-only">Loading floor plans</p>
          {layout === 'rows' && (
            <div className="hidden md:block">
              <RowsSkeleton />
            </div>
          )}
          <div className={cn(layout === 'rows' && 'md:hidden')}>
            <CardsSkeleton />
          </div>
          {content.terms && <Terms terms={content.terms} />}
          {content.contact && <ContactPrompt contact={content.contact} />}
        </>
      );
    case 'failed':
    case 'empty':
      return (
        <div className="py-lg text-center">
          <p>{content.status === 'failed' ? 'Floor plans did not load.' : 'Floor plans are coming soon.'}</p>
          {content.contact && (
            <Button asChild variant="link" className="mt-sm whitespace-normal text-foreground underline underline-offset-4 [overflow-wrap:anywhere] hover:no-underline">
              <a href={content.contact.href} {...tabProps(content.contact.newTab)}>{content.contact.label}</a>
            </Button>
          )}
        </div>
      );
    case 'ready':
      return (
        <>
          {content.notice && <p className="mb-md text-muted-foreground">{content.notice.label}</p>}
          <div key={layout} className="animate-in fade-in-0 motion-reduce:animate-none">
            {!content.plans.length ? (
              <NoMatches refine={content.refine} />
            ) : layout === 'rows' ? (
              <>
                <div className="hidden md:block">
                  <Rows plans={content.plans} today={content.today} propertyFees={content.propertyFees} />
                </div>
                <div className="md:hidden">
                  <Cards plans={content.plans} today={content.today} propertyFees={content.propertyFees} />
                </div>
              </>
            ) : (
              <Cards plans={content.plans} today={content.today} propertyFees={content.propertyFees} />
            )}
          </div>
          {content.terms && <Terms terms={content.terms} />}
          {content.contact && <ContactPrompt contact={content.contact} />}
        </>
      );
    default: {
      const unhandled: never = content;
      return unhandled;
    }
  }
}

function NoMatches({ refine }: { refine: Refinement | null | undefined }): ReactElement {
  return (
    <div className="py-2xl text-center">
      <p className="font-heading text-2xl text-foreground">No floor plans match these filters</p>
      <p className="mt-xs text-sm text-muted-foreground">Try a wider rent or size range, or a later move-in date.</p>
      {refine && (
        <Button type="button" variant="outline" className={cn('mt-md', OUTLINE)} onClick={() => refine.select(NO_FILTERS)}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

function isAvailable(floorplan: ReadyPlan): boolean {
  return floorplan.availability.kind === 'listed' || floorplan.availability.kind === 'reported';
}

function planKey(floorplan: ReadyPlan): string {
  return String(floorplan.id ?? floorplan.floorplan_name ?? floorplan.factsLabel);
}

// ---------------------------------------------------------------------------
// Rows: one list in the visitor's sort order, under one row of column labels.
// Plans with homes expand in place to their units and costs. Plans with none
// follow, dimmed, each with its own waitlist action.
// ---------------------------------------------------------------------------

function Rows({ plans, today, propertyFees }: { plans: readonly ReadyPlan[]; today: IsoDate; propertyFees: readonly Fee[] }): ReactElement {
  const [open, setOpen] = useState<string[]>([]);
  const [target, setTarget] = useState<DialogTarget | null>(null);
  const ordered = [...plans.filter(isAvailable), ...plans.filter((plan) => !isAvailable(plan))];
  const toggle = (value: string) =>
    setOpen((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));
  return (
    <>
      <RowHeadings />
      <Accordion type="multiple" value={open} onValueChange={setOpen} asChild>
        <ul>
          {ordered.map((floorplan, index) => (
            <PlanRow
              key={planKey(floorplan)}
              value={planKey(floorplan)}
              floorplan={floorplan}
              today={today}
              propertyFees={propertyFees}
              waitlisted={!isAvailable(floorplan)}
              onToggle={() => toggle(planKey(floorplan))}
              onOpen={() => setTarget({ index, tour: null })}
              onFees={() => setTarget({ index, tour: null, fees: true })}
              onTour={(tour) => setTarget({ index, tour: tour.href })}
            />
          ))}
        </ul>
      </Accordion>
      <PlanDialog plans={ordered} today={today} propertyFees={propertyFees} target={target} setTarget={setTarget} />
    </>
  );
}

function RowHeadings(): ReactElement {
  return (
    <div aria-hidden className={cn(ROW_GRID, 'border-b border-border pb-sm', EYEBROW)}>
      <span>Plan</span>
      <span />
      <span>Availability</span>
      <span className="text-right">Rent</span>
    </div>
  );
}

/** A placeholder bar, tinted from the text color like the other neutral panels. */
function Bone({ className }: { className?: string }): ReactElement {
  return <span aria-hidden className={cn('block animate-pulse rounded-md motion-reduce:animate-none', TINT, className)} />;
}

/** A bar centered in one line of text. `text` is the loaded text's style, and an invisible character gives the line that style's height in any theme. */
function TextBone({ text, className }: { text: string; className: string }): ReactElement {
  return (
    <span aria-hidden className={cn('flex items-center', text)}>
      {'\u200b'}
      <Bone className={className} />
    </span>
  );
}

/** Rows hold their loaded height. The real row's thumb sets it, so the bone thumb matches. */
function RowsSkeleton(): ReactElement {
  return (
    <>
      <RowHeadings />
      <ul aria-hidden>
        {[0, 1, 2, 3].map((key) => (
          <li key={key} className="list-none border-b border-border">
            <div className={cn(ROW_GRID, 'py-lg')}>
              <Bone className="h-16 w-20" />
              <div className="min-w-0">
                <TextBone text="text-lg" className="h-4 w-40" />
                <TextBone text="text-sm" className="h-3 w-48 max-w-full" />
              </div>
              <TextBone text="text-sm" className="h-3 w-36" />
              <div className="flex justify-end">
                <TextBone text="font-heading text-2xl" className="h-5 w-24" />
              </div>
              <div className="flex items-center justify-end gap-sm">
                <Bone className="h-10 w-24" />
                <Bone className="h-8 w-8 rounded-full" />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/** One row of cards at every width: one on phones, two on tablets, three on desktops, plus a second row past the fold. */
function CardsSkeleton(): ReactElement {
  const shown = ['', '', '', 'hidden md:block', 'hidden xl:block', 'hidden xl:block'];
  return (
    <ul aria-hidden className="grid grid-cols-1 gap-lg md:grid-cols-2 xl:grid-cols-3">
      {shown.map((visibility, key) => (
        <li key={key} className={cn('list-none', visibility)}>
          <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-background">
            <div className="relative aspect-[4/3] w-full border-b border-border">
              <Bone className="absolute inset-0 rounded-none" />
              <span className="absolute left-md top-md flex h-7 w-32 items-center gap-sm rounded-full border border-border bg-background px-sm">
                <Bone className="h-2 w-2 shrink-0 rounded-full" />
                <Bone className="h-2.5 flex-1" />
              </span>
            </div>
            <div className="flex flex-1 flex-col gap-md p-lg">
              <div className="flex items-start justify-between gap-md">
                <div className="min-w-0 flex-1">
                  <TextBone text="font-heading text-2xl" className="h-5 w-2/3" />
                  <TextBone text="text-sm" className="h-3 w-1/2" />
                </div>
                <TextBone text="font-heading text-xl md:text-2xl" className="h-5 w-20" />
              </div>
              <Bone className="h-[calc(1lh+6px)] w-40 max-w-full rounded-xl text-xs leading-snug" />
              <div className="mt-auto flex gap-sm">
                <Bone className="h-10 flex-1" />
                <Bone className="h-10 flex-1" />
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function PlanRow({
  value,
  floorplan,
  today,
  propertyFees,
  waitlisted,
  onToggle,
  onOpen,
  onFees,
  onTour,
}: {
  value: string;
  floorplan: ReadyPlan;
  today: IsoDate;
  propertyFees: readonly Fee[];
  /** No homes to rent. The row dims, shows no rent, does not expand, and offers the waitlist instead. */
  waitlisted: boolean;
  onToggle: () => void;
  /** Opens the plan dialog: the drawing, tours, and the home picker. */
  onOpen: () => void;
  /** Opens the plan dialog scrolled to its fees. */
  onFees: () => void;
  onTour: (tour: Tour) => void;
}): ReactElement {
  /** Counted for the home the dialog opens on, so the link matches the list it opens. */
  const fees = feesFor(floorplan, floorplan.listed[0]?.unit ?? null, propertyFees);
  const detailed = !waitlisted && (hasDetails(floorplan) || fees.count > 0);
  const toggle = (event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (!detailed || target.closest('a, button')) return;
    onToggle();
  };
  return (
    <AccordionItem value={value} asChild>
      <li className="list-none border-b border-border animate-in fade-in-0 duration-300 motion-reduce:animate-none">
        <div className={cn(ROW_GRID, detailed && 'cursor-pointer', 'py-lg')} onClick={toggle}>
          <div className={cn(waitlisted && 'opacity-60')}>
            <Button
              type="button"
              variant="ghost"
              onClick={onOpen}
              aria-haspopup="dialog"
              className="group/thumb relative h-auto cursor-zoom-in rounded-md p-0 hover:bg-transparent"
            >
              <span className="sr-only">{`Open ${floorplan.floorplan_name ?? 'this plan'}`}</span>
              <PlanThumb floorplan={floorplan} />
              <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--vibe-color-background)_75%,transparent)] text-foreground opacity-0 transition-opacity group-hover/thumb:opacity-100 group-focus-visible/thumb:opacity-100"
              >
                <Expand size={16} />
              </span>
            </Button>
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-sm gap-y-xs">
              <h3 className={cn('line-clamp-2 min-w-0 text-lg [overflow-wrap:anywhere]', waitlisted ? 'font-medium text-muted-foreground' : 'font-semibold text-foreground')}>{floorplan.floorplan_name}</h3>
              {floorplan.runningSpecials.length > 0 && <SpecialChip offers={floorplan.runningSpecials} />}
            </div>
            <p className="text-sm text-muted-foreground">{factsLine(floorplan)}</p>
          </div>
          <div>
            <AvailabilityLabel floorplan={floorplan} />
          </div>
          <div className="text-right">
            {!waitlisted && <FromRent floorplan={floorplan} />}
          </div>
          <AccordionHeader asChild>
            <div className="flex items-center justify-end gap-sm">
              {waitlisted ? (
                <PlanAction floorplan={floorplan} label="Join the waitlist" className={OUTLINE} />
              ) : (
                <PlanAction floorplan={floorplan} />
              )}
              <AccordionTrigger
                aria-label={`Details for ${floorplan.floorplan_name ?? 'this plan'}`}
                className={cn('group rounded-full bg-secondary p-sm text-secondary-foreground transition-opacity hover:opacity-90', (!detailed || waitlisted) && 'invisible')}
              >
                <ChevronDownIcon aria-hidden size={16} className="transition-transform group-data-[state=open]:rotate-180" />
              </AccordionTrigger>
            </div>
          </AccordionHeader>
        </div>
        <AccordionContent>
          <div className="pb-lg">
            {detailed && (
              <div className="flex gap-xl">
                <div className="min-w-0 flex-1">
                  {floorplan.listed.length > 0 ? (
                    <UnitTable floorplan={floorplan} today={today} onTour={onTour} />
                  ) : (
                    <p className="text-sm text-muted-foreground">No homes are listed for this plan right now.</p>
                  )}
                </div>
                <CostPanel floorplan={floorplan} onTour={onTour} className="w-80 shrink-0">
                  {fees.count > 0 && <RowFees floorplan={floorplan} propertyFees={propertyFees} fees={fees} onOpen={onFees} />}
                </CostPanel>
              </div>
            )}
          </div>
        </AccordionContent>
      </li>
    </AccordionItem>
  );
}

function hasDetails(floorplan: ReadyPlan): boolean {
  return floorplan.listed.length > 0 || hasCosts(floorplan);
}

function panelTour(floorplan: ReadyPlan): ReadyPlan['tour'] {
  const tour = floorplan.tour;
  if (!tour) return null;
  if (floorplan.listed.some((row) => row.tour?.href === tour.href)) return null;
  return tour;
}

function hasCosts(floorplan: ReadyPlan): boolean {
  return Boolean(floorplan.deposit || panelTour(floorplan) || floorplan.pets.length || rentRange(floorplan));
}

function rentRange(floorplan: ReadyPlan): string | null {
  const rent = floorplan.rent;
  if (rent.kind !== 'priced' || rent.range.min === rent.range.max) return null;
  return formatRange(rent.range);
}

function factsLine(floorplan: ReadyPlan): string {
  return floorplan.factsLabel.replace(/, /g, ' · ');
}

function planImage(floorplan: ReadyPlan) {
  const url = floorplan.floorplan_image_url;
  if (!url) return null;
  const media = floorplan.floorplan_image_url_media;
  return {
    url,
    alt: floorplan.floorplan_name ? `${floorplan.floorplan_name} floor plan` : 'Floor plan',
    width: media?.width || undefined,
    height: media?.height || undefined,
  };
}

function PlanThumb({ floorplan }: { floorplan: ReadyPlan }): ReactElement {
  const image = planImage(floorplan);
  if (image) {
    return (
      <div aria-hidden className="relative h-16 w-20 overflow-hidden rounded-md border border-border bg-background">
        <VibesitesImage {...image} fill objectFit="contain" sizes="80px" />
      </div>
    );
  }
  return (
    <div aria-hidden className={cn('flex h-16 w-20 flex-col items-center justify-center rounded-md text-foreground', TINT)}>
      <ThumbFallback floorplan={floorplan} />
    </div>
  );
}

/** Square footage when the plan has it, else the bedroom count. The plan name is already beside it. */
function ThumbFallback({ floorplan, large = false }: { floorplan: ReadyPlan; large?: boolean }): ReactElement {
  const sqft = floorplan.sqft;
  if (sqft) {
    return (
      <>
        <span className={cn('font-heading leading-none', large ? 'text-5xl' : 'text-xl')}>
          {formatNumber(sqft.min)}
        </span>
        <span className={cn('mt-xs', EYEBROW, 'text-foreground', !large && 'text-[10px]')}>sq ft</span>
      </>
    );
  }
  return <span className={cn('font-heading', large ? 'text-5xl' : 'text-base')}>{bedsHeading(floorplan)}</span>;
}

// ---------------------------------------------------------------------------
// Cards: a grid of available plans, each opening a quick view, then a compact
// list of plans with nothing available.
// ---------------------------------------------------------------------------

function Cards({ plans, today, propertyFees }: { plans: readonly ReadyPlan[]; today: IsoDate; propertyFees: readonly Fee[] }): ReactElement {
  const available = plans.filter(isAvailable);
  const waitlisted = plans.filter((plan) => !isAvailable(plan));
  const ordered = [...available, ...waitlisted];
  const [target, setTarget] = useState<DialogTarget | null>(null);
  return (
    <>
      {available.length > 0 && (
        <ul className="grid grid-cols-1 gap-lg md:grid-cols-2 xl:grid-cols-3">
          {available.map((floorplan, index) => (
            <PlanCard key={planKey(floorplan)} floorplan={floorplan} priority={index === 0} onOpen={() => setTarget({ index, tour: null })} />
          ))}
        </ul>
      )}
      {waitlisted.length > 0 && (
        <div className={cn(available.length > 0 && 'mt-2xl')}>
          <div className="mb-md flex flex-wrap items-baseline gap-x-md">
            <h3 className="font-heading text-2xl text-foreground">No current availability</h3>
            <p className="text-sm text-muted-foreground">
              {`${waitlisted.length} ${waitlisted.length === 1 ? 'plan' : 'plans'}. Contact us and we will reach out when one opens.`}
            </p>
          </div>
          <ul className="grid grid-cols-1 gap-md sm:grid-cols-2 xl:grid-cols-4">
            {waitlisted.map((floorplan, index) => (
              <li key={planKey(floorplan)} className="list-none">
                <Button
                  type="button"
                  variant="ghost"
                  aria-haspopup="dialog"
                  onClick={() => setTarget({ index: available.length + index, tour: null })}
                  className={cn('h-auto w-full justify-start gap-md whitespace-normal rounded-lg border border-border p-sm text-left font-normal hover:bg-transparent', TINT_HOVER)}
                >
                  <PlanThumb floorplan={floorplan} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-foreground">{floorplan.floorplan_name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{factsLine(floorplan)}</span>
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <PlanDialog plans={ordered} today={today} propertyFees={propertyFees} target={target} setTarget={setTarget} />
    </>
  );
}

/** The first card's drawing is usually the largest thing on a phone's first screen, so it loads before the rest. */
function PlanCard({ floorplan, priority, onOpen }: { floorplan: ReadyPlan; priority: boolean; onOpen: () => void }): ReactElement {
  const image = planImage(floorplan);
  const homes = floorplan.listed.length;
  return (
    <li className="list-none animate-in fade-in-0 duration-300 motion-reduce:animate-none">
      <div className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border bg-background transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-ring">
        <div className="relative aspect-[4/3] w-full overflow-hidden border-b border-border bg-background">
          {image ? (
            <span className="absolute inset-sm">
              <VibesitesImage {...image} fill objectFit="contain" sizes={CARD_SIZES} priority={priority} />
            </span>
          ) : (
            <span aria-hidden className={cn('absolute inset-0 flex flex-col items-center justify-center text-foreground', TINT)}>
              <ThumbFallback floorplan={floorplan} large />
            </span>
          )}
          <span className="absolute left-md top-md">
            <AvailabilityLabel floorplan={floorplan} chip />
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-md p-lg">
          <div className="flex items-start justify-between gap-md">
            <div className="min-w-0">
              <h3 className="font-heading text-2xl text-foreground">
                <Button type="button" variant="ghost" onClick={onOpen} aria-haspopup="dialog" className="h-auto whitespace-normal rounded-none p-0 text-left text-2xl font-heading font-normal text-foreground after:absolute after:inset-0 hover:bg-transparent hover:text-foreground focus-visible:ring-0 focus-visible:ring-offset-0">
                  <span className="line-clamp-2 [overflow-wrap:anywhere]">{floorplan.floorplan_name}</span>
                </Button>
              </h3>
              <p className="text-sm text-muted-foreground">{factsLine(floorplan)}</p>
            </div>
            <FromRent floorplan={floorplan} />
          </div>
          {floorplan.runningSpecials.length > 0 && (
            <div>
              <SpecialChip offers={floorplan.runningSpecials} />
            </div>
          )}
          <div className="mt-auto flex gap-sm">
            <PlanAction floorplan={floorplan} size="default" className="relative z-10 flex-1" />
            <Button type="button" variant="outline" className={cn('relative z-10 flex-1', OUTLINE)} onClick={onOpen} aria-haspopup="dialog">
              {homes > 0 ? `See ${homes} ${homes === 1 ? 'home' : 'homes'}` : 'Details'}
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
}

/** Which plan the dialog shows, and the tour to open it on. A null tour opens the details, scrolled to the fees when `fees` is set. */
type DialogTarget = { readonly index: number; readonly tour: string | null; readonly fees?: boolean };

/** One dialog for a list of plans. Prev and next walk `plans` in order. */
function PlanDialog({
  plans,
  today,
  propertyFees,
  target,
  setTarget,
}: {
  plans: readonly ReadyPlan[];
  today: IsoDate;
  propertyFees: readonly Fee[];
  target: DialogTarget | null;
  setTarget: (target: DialogTarget | null) => void;
}): ReactElement {
  const current = target == null ? null : plans[target.index] ?? null;
  return (
    <Dialog open={current != null} onOpenChange={(open) => !open && setTarget(null)}>
      {current && target && (
        <QuickView
          floorplan={current}
          today={today}
          propertyFees={propertyFees}
          startTour={target.tour}
          startFees={target.fees === true}
          position={{ index: target.index, total: plans.length }}
          previous={target.index > 0 ? plans[target.index - 1] ?? null : null}
          next={plans[target.index + 1] ?? null}
          go={(step) => setTarget({ index: target.index + step, tour: null })}
        />
      )}
    </Dialog>
  );
}

/** The dialog stays mounted across prev and next. Only the body is keyed by plan, so its unit choice and mode reset. */
function QuickView({
  floorplan,
  today,
  propertyFees,
  startTour,
  startFees,
  position,
  previous,
  next,
  go,
}: {
  floorplan: ReadyPlan;
  today: IsoDate;
  propertyFees: readonly Fee[];
  startTour: string | null;
  startFees: boolean;
  position: { index: number; total: number };
  previous: ReadyPlan | null;
  next: ReadyPlan | null;
  go: (step: -1 | 1) => void;
}): ReactElement {
  const roomy = albumsOf(floorplan, null).length > 0;
  return (
    <DialogContent
      className={cn(
        'flex h-[100dvh] max-h-[100dvh] max-w-none flex-col gap-0 overflow-hidden p-0 sm:rounded-xl md:h-[min(90dvh,50rem)] [&>button:last-child]:z-20',
        roomy ? 'md:max-w-6xl' : 'md:max-w-2xl',
      )}
      /** Opened for its fees, the fee heading takes focus instead of the first control. */
      onOpenAutoFocus={startFees ? (event) => event.preventDefault() : undefined}
    >
      <QuickViewBody
        key={`${planKey(floorplan)}:${startTour ?? ''}:${startFees}`}
        floorplan={floorplan}
        today={today}
        propertyFees={propertyFees}
        startTour={startTour}
        startFees={startFees}
        position={position}
        previous={previous}
        next={next}
        go={go}
      />
    </DialogContent>
  );
}

type Picture = NonNullable<ReturnType<typeof planImage>>;

type AlbumItem =
  | { readonly kind: 'image'; readonly image: Picture; readonly title: string | null; readonly description: string | null }
  | { readonly kind: 'tour'; readonly tour: Tour; readonly embed: TourEmbed };

/** One group of media the viewer pages through. Plan albums come first, then one per listed home. Empty albums are left out. */
type Album = { readonly key: string; readonly label: string; readonly items: readonly AlbumItem[] } & (
  | { readonly kind: 'drawing' | 'photos' | 'tours' }
  | { readonly kind: 'home'; readonly home: ListedUnit }
);

/** The album and item on screen. */
type Spot = { readonly album: string; readonly index: number };

/** The selected home's album leads the homes. */
function albumsOf(floorplan: ReadyPlan, selected: ListedUnit | null): Album[] {
  const drawing = planImage(floorplan);
  const photo = (item: PlanPhoto): AlbumItem => ({
    kind: 'image',
    image: { url: item.url, alt: item.alt, width: item.media?.width || undefined, height: item.media?.height || undefined },
    title: item.title,
    description: item.description,
  });
  const tour = (item: Tour): AlbumItem => ({ kind: 'tour', tour: item, embed: tourEmbed(item.href) });
  const homes = selected ? [selected, ...floorplan.listed.filter((home) => home !== selected)] : floorplan.listed;
  const albums: Album[] = [
    { key: 'drawing', kind: 'drawing', label: 'Floor plan', items: drawing ? [{ kind: 'image', image: drawing, title: null, description: null }] : [] },
    { key: 'photos', kind: 'photos', label: 'Photos', items: floorplan.photos.map(photo) },
    { key: 'tours', kind: 'tours', label: 'Virtual tours', items: floorplan.tours.map(tour) },
    ...homes.map((home): Album => ({ key: homeKey(home), kind: 'home', label: unitName(home), home, items: [...home.photos.map(photo), ...home.tours.map(tour)] })),
  ];
  return albums.filter((album) => album.items.length > 0);
}

function homeKey(home: ListedUnit): string {
  return `home:${unitName(home)}`;
}

function tourSpot(albums: readonly Album[], href: string): Spot | null {
  for (const album of albums) {
    const index = album.items.findIndex((item) => item.kind === 'tour' && item.tour.href === href);
    if (index >= 0) return { album: album.key, index };
  }
  return null;
}

/** "Drawing", "12 photos", "6 photos, 1 tour". */
function albumCount(album: Album): string {
  if (album.kind === 'drawing') return 'Drawing';
  const photos = album.items.filter((item) => item.kind === 'image').length;
  return mediaCount(photos, album.items.length - photos);
}

function mediaCount(photos: number, tours: number): string {
  return [photos && `${photos} ${photos === 1 ? 'photo' : 'photos'}`, tours && `${tours} ${tours === 1 ? 'tour' : 'tours'}`].filter(Boolean).join(', ');
}

function moveLine(home: ListedUnit): string {
  return `${unitName(home)}, ${home.moveIn === 'now' ? 'move in now' : `move in ${home.moveInLabel}`}`;
}

function QuickViewBody({
  floorplan,
  today,
  propertyFees,
  startTour,
  startFees,
  position,
  previous,
  next,
  go,
}: {
  floorplan: ReadyPlan;
  today: IsoDate;
  propertyFees: readonly Fee[];
  startTour: string | null;
  startFees: boolean;
  position: { index: number; total: number };
  previous: ReadyPlan | null;
  next: ReadyPlan | null;
  go: (step: -1 | 1) => void;
}): ReactElement {
  const [selected, setSelected] = useState<ListedUnit | null>(floorplan.listed[0] ?? null);
  /** Null is the details. A spot is the viewer, open on that album and item. */
  const [spot, setSpot] = useState<Spot | null>(() => (startTour ? tourSpot(albumsOf(floorplan, floorplan.listed[0] ?? null), startTour) : null));
  /** Opened from a row's fee link, the fees take the view once. Picking another home after that leaves the scroll alone. */
  const [revealFees, setRevealFees] = useState(startFees);
  const albums = albumsOf(floorplan, selected);
  const openTour = (tour: Tour) => {
    const found = tourSpot(albums, tour.href);
    if (found) setSpot(found);
  };
  if (spot && albums.length > 0) {
    return <Viewer floorplan={floorplan} albums={albums} spot={spot} selected={selected} show={setSpot} back={() => setSpot(null)} />;
  }
  const spots = albums.flatMap((album) => album.items.map((item, index) => ({ album: album.key, index, item })));
  const cover = spots.find((entry) => entry.item.kind === 'image');
  const image = cover?.item.kind === 'image' ? cover.item.image : null;
  const expand = cover?.album === 'drawing' ? 'Expand floor plan' : 'Expand photo';
  const firstTour = spots.find((entry) => entry.item.kind === 'tour');
  const showCover = () => cover && setSpot({ album: cover.album, index: cover.index });
  const photos = floorplan.photos.length;
  const tours = floorplan.tours.length;
  const homeAlbum = selected ? albums.find((album) => album.key === homeKey(selected)) : undefined;
  const fees = feesFor(floorplan, selected?.unit ?? null, propertyFees);
  /**
   * Phones scroll the drawing, details, and fees as one page. Wider screens keep the drawing beside the details,
   * which scroll on their own. Either way the title sticks to the top and the price and Apply to the bottom, so a
   * short viewport (a phone on its side) can always reach them.
   */
  return (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain animate-in fade-in-0 motion-reduce:animate-none md:flex md:overflow-hidden">
      {albums.length > 0 && (
        <div className="relative h-56 shrink-0 border-b border-border bg-background md:h-auto md:w-1/2 md:border-b-0 md:border-r">
          {image ? (
            <Button type="button" variant="ghost" onClick={showCover} aria-label={expand} className="absolute inset-md h-auto w-auto cursor-zoom-in rounded-none p-0 hover:bg-transparent md:inset-xl">
              <VibesitesImage {...image} fill objectFit="contain" sizes="(min-width: 768px) 576px, 100vw" />
            </Button>
          ) : (
            firstTour?.item.kind === 'tour' && (
              <div className={cn('absolute inset-0 flex flex-col items-center justify-center gap-sm p-lg text-center', TINT)}>
                <Button type="button" variant="ghost" onClick={() => setSpot({ album: firstTour.album, index: firstTour.index })} aria-label={`Open ${firstTour.item.tour.label}`} className="h-16 w-16 rounded-full bg-primary p-0 text-primary-foreground shadow-md hover:scale-105 hover:bg-primary hover:text-primary-foreground">
                  <Play aria-hidden size={22} />
                </Button>
                <p className="mt-sm font-semibold text-foreground">{firstTour.item.tour.label}</p>
              </div>
            )
          )}
          <div className="absolute bottom-md left-md flex flex-wrap gap-xs">
            {image && (
              <Button type="button" variant="outline" size="icon" onClick={showCover} aria-label={expand} title={expand}>
                <Expand aria-hidden size={16} />
              </Button>
            )}
            {photos > 0 && (
              <Button type="button" variant="outline" onClick={() => setSpot({ album: 'photos', index: 0 })}>
                {photos === 1 ? '1 photo' : `${photos} photos`}
              </Button>
            )}
            {tours > 0 && image && (
              <Button type="button" variant="outline" onClick={() => setSpot({ album: 'tours', index: 0 })}>
                <Play aria-hidden size={14} className="mr-xs" />
                {tours === 1 ? '1 tour' : `${tours} tours`}
              </Button>
            )}
            {homeAlbum && (
              <Button type="button" variant="outline" onClick={() => setSpot({ album: homeAlbum.key, index: 0 })}>
                {`Home ${homeAlbum.label}`}
                <span className="ml-xs tabular-nums text-muted-foreground">{homeAlbum.items.length}</span>
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="flex min-h-full flex-col md:min-h-0 md:flex-1 md:overflow-y-auto md:overscroll-contain">
        <div className="sticky top-0 z-10 border-b border-border bg-background px-lg pb-sm pt-lg pr-3xl md:border-b-0">
          <DialogTitle className="font-heading text-3xl text-foreground md:text-4xl">{floorplan.floorplan_name}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{factsLine(floorplan)}</DialogDescription>
        </div>
        {floorplan.description && <p className="px-lg pb-md text-sm text-muted-foreground">{floorplan.description}</p>}
        <div className="flex flex-col gap-md border-b border-border px-lg pb-lg">
          <dl className="flex flex-wrap gap-x-lg gap-y-sm text-sm">
            <Fact term="Rent">{floorplan.rent.label}</Fact>
            <Fact term="Available">{floorplan.availability.label}</Fact>
            {floorplan.deposit && <Fact term="Deposit">{floorplan.deposit.replace(/ deposit$/, '')}</Fact>}
          </dl>
          {floorplan.planSpecials.length > 0 && (
            <div>
              <p className={cn('mb-xs', EYEBROW)}>{floorplan.listed.length > 0 ? 'Specials on every home' : 'Specials'}</p>
              <Specials offers={floorplan.planSpecials} today={today} className="flex flex-col gap-xs text-sm" />
            </div>
          )}
        </div>
        <div className="flex-1 p-lg">
          {floorplan.listed.length > 0 ? (
            <UnitPicker floorplan={floorplan} today={today} selected={selected} select={setSelected} />
          ) : (
            <p className="text-sm text-muted-foreground">
              {floorplan.availability.kind === 'reported'
                ? `${floorplan.availability.label}. Contact us to pick your home.`
                : `${floorplan.availability.label}. Contact us to hear when a home opens.`}
            </p>
          )}
          <AmenityList floorplan={floorplan} selected={selected} select={setSelected} />
          {hasCosts(floorplan) && <CostPanel floorplan={floorplan} onTour={openTour} className="mt-lg" />}
          <HomeFees key={selected ? unitName(selected) : 'plan'} fees={fees} reveal={revealFees} setReveal={setRevealFees} />
        </div>
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-sm border-t border-border bg-background p-md md:px-lg">
          <div className="mr-auto min-w-0">
            {selected ? (
              <>
                <p className="truncate text-xs text-muted-foreground">{moveLine(selected)}</p>
                <p className="font-heading text-2xl text-foreground">{selected.rentLabel}</p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">{`Plan ${position.index + 1} of ${position.total}`}</p>
            )}
          </div>
          <Button type="button" size="icon" variant="ghost" disabled={!previous} onClick={() => go(-1)} aria-label="Previous plan">
            <ChevronLeftIcon aria-hidden size={16} />
          </Button>
          <Button type="button" size="icon" variant="ghost" disabled={!next} onClick={() => go(1)} aria-label="Next plan">
            <ChevronRightIcon aria-hidden size={16} />
          </Button>
          {selected?.unit.apply_url ? (
            <Button asChild>
              <a href={selected.unit.apply_url} aria-label={`Apply for ${unitName(selected)}`}>
                Apply<span className="hidden whitespace-pre sm:inline">{` for ${unitName(selected)}`}</span>
              </a>
            </Button>
          ) : (
            <PlanAction floorplan={floorplan} size="default" />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The same dialog in viewer mode, one album at a time. Desktop lists the albums in a rail on the left, phones in a
 * row of tabs. An album of one item drops its counter, arrows, and filmstrip. Back returns to the details. The
 * dialog's own close button closes everything.
 */
function Viewer({
  floorplan,
  albums,
  spot,
  selected,
  show,
  back,
}: {
  floorplan: ReadyPlan;
  albums: readonly Album[];
  spot: Spot;
  selected: ListedUnit | null;
  show: (spot: Spot) => void;
  back: () => void;
}): ReactElement {
  const album = albums.find((entry) => entry.key === spot.album) ?? albums[0];
  const total = album.items.length;
  const index = Math.min(spot.index, total - 1);
  const item = album.items[index];
  const many = total > 1;
  const step = (by: -1 | 1) => show({ album: album.key, index: (index + by + total) % total });
  const open = (key: string) => show({ album: key, index: 0 });
  const plan = albums.filter((entry) => entry.kind !== 'home');
  const homes = albums.filter((entry) => entry.kind === 'home');
  const caption = item.kind === 'image' ? item : { title: item.tour.label, description: text(item.tour.virtual_tour_description) };
  const strip = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const list = strip.current;
    const tile = list?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!list || !tile) return;
    if (tile.offsetLeft < list.scrollLeft) list.scrollLeft = tile.offsetLeft;
    else if (tile.offsetLeft + tile.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollLeft = tile.offsetLeft + tile.offsetWidth - list.clientWidth;
  }, [album.key, index]);
  return (
    <div className="flex min-h-0 flex-1 flex-col animate-in fade-in-0 motion-reduce:animate-none">
      <div className="flex items-center gap-sm border-b border-border py-sm pl-sm pr-3xl">
        <Button type="button" variant="ghost" onClick={back} className="shrink-0 px-sm">
          <ChevronLeftIcon aria-hidden size={16} />
          <span className="ml-xs hidden sm:inline">Back to details</span>
          <span className="sr-only sm:hidden">Back to details</span>
        </Button>
        <div className="min-w-0 flex-1 md:text-center">
          <DialogTitle className="truncate font-heading text-lg text-foreground md:text-xl">{floorplan.floorplan_name}</DialogTitle>
          <DialogDescription className="truncate text-xs text-muted-foreground md:text-sm">
            {album.kind === 'home' ? `Home ${album.label}` : album.label}
          </DialogDescription>
        </div>
        <span aria-hidden className="hidden w-36 shrink-0 sm:block" />
      </div>
      {albums.length > 1 && (
        <nav aria-label="Albums" className="flex shrink-0 gap-xs overflow-x-auto overscroll-contain border-b border-border px-md py-sm md:hidden">
          {albums.map((entry) => {
            const active = entry.key === album.key;
            return (
              <Button
                key={entry.key}
                type="button"
                variant="outline"
                size="sm"
                aria-pressed={active}
                onClick={() => open(entry.key)}
                className={cn('shrink-0 rounded-full', active && 'border-foreground bg-foreground text-background hover:bg-foreground hover:text-background')}
              >
                {entry.kind === 'tours' ? 'Tours' : entry.label}
                {entry.items.length > 1 && <span className="ml-xs tabular-nums opacity-70">{entry.items.length}</span>}
              </Button>
            );
          })}
        </nav>
      )}
      <div className="flex min-h-0 flex-1">
        {albums.length > 1 && (
          <nav aria-label="Albums" className="hidden w-64 shrink-0 flex-col gap-lg overflow-y-auto overscroll-contain border-r border-border p-md md:flex">
            {[
              { title: 'This plan', entries: plan },
              { title: 'Homes', entries: homes },
            ].map(
              (section) =>
                section.entries.length > 0 && (
                  <div key={section.title}>
                    <p className={cn('mb-xs px-sm', EYEBROW)}>{section.title}</p>
                    <ul className="flex flex-col gap-xs">
                      {section.entries.map((entry) => {
                        const active = entry.key === album.key;
                        return (
                          <li key={entry.key}>
                            <Button
                              type="button"
                              variant="ghost"
                              aria-pressed={active}
                              onClick={() => open(entry.key)}
                              className={cn(
                                'h-auto w-full justify-start gap-sm whitespace-normal rounded-lg border px-sm py-xs text-left font-normal hover:bg-transparent',
                                active ? cn('border-foreground', TINT) : cn('border-transparent', TINT_HOVER),
                              )}
                            >
                              <AlbumThumb album={entry} />
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium text-foreground">
                                  {entry.label}
                                  {entry.kind === 'home' && homes.length > 1 && entry.home === selected && <span className="font-normal text-muted-foreground"> · your pick</span>}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">{albumCount(entry)}</span>
                              </span>
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ),
            )}
          </nav>
        )}
        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto overscroll-contain md:gap-md md:p-lg">
          {album.kind === 'home' && <HomeBar floorplan={floorplan} home={album.home} count={albumCount(album)} />}
          <div className="relative min-h-56 flex-1 overflow-hidden bg-background md:rounded-lg">
            {item.kind === 'tour' ? (
              <TourStage key={`${album.key}:${index}`} tour={item.tour} embed={item.embed} />
            ) : album.kind === 'drawing' ? (
              <ImageStage key={`${album.key}:${index}`} image={item.image} />
            ) : (
              <span className={cn('absolute inset-0', TINT)}>
                <VibesitesImage key={`${album.key}:${index}`} {...item.image} fill objectFit="contain" />
              </span>
            )}
            {many && (
              <>
                <span className="pointer-events-none absolute left-md top-md z-10 rounded-full bg-[color-mix(in_srgb,var(--vibe-color-text)_75%,transparent)] px-sm py-[2px] text-xs font-medium tabular-nums text-background">
                  {`${index + 1} / ${total}`}
                </span>
                <Button type="button" variant="outline" size="icon" onClick={() => step(-1)} aria-label="Previous" className="absolute left-md top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full bg-background shadow-md">
                  <ChevronLeftIcon aria-hidden size={16} />
                </Button>
                <Button type="button" variant="outline" size="icon" onClick={() => step(1)} aria-label="Next" className="absolute right-md top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full bg-background shadow-md">
                  <ChevronRightIcon aria-hidden size={16} />
                </Button>
              </>
            )}
          </div>
          {(caption.title || caption.description) && (
            <p className={cn('shrink-0 px-md pt-sm text-sm md:p-0', !many && 'pb-md')}>
              {caption.title && <span className="mr-sm font-semibold text-foreground">{caption.title}</span>}
              {caption.description && <span className="text-muted-foreground">{caption.description}</span>}
            </p>
          )}
          {many && (
            <ul ref={strip} aria-label={album.kind === 'home' ? `Home ${album.label}` : album.label} className="relative flex shrink-0 gap-xs overflow-x-auto overscroll-contain p-md md:gap-sm md:p-0">
              {album.items.map((entry, position) => (
                <li key={position} className="shrink-0">
                  <StripTile item={entry} active={position === index} onClick={() => show({ album: album.key, index: position })} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

const TOUR_KIND: Record<TourEmbed['kind'], string> = { '3d': '3D tour', video: 'Video', link: 'Tour' };

/** Photos stack two deep, a tour album shows a play tile, the drawing shows itself. */
function AlbumThumb({ album }: { album: Album }): ReactElement {
  const [front, back] = album.items;
  const face = (item: AlbumItem | undefined) =>
    item?.kind === 'image' ? <VibesitesImage {...item.image} fill objectFit={album.kind === 'drawing' ? 'contain' : 'cover'} sizes="56px" /> : item ? <Play size={12} /> : null;
  const tile = 'absolute flex h-9 w-12 items-center justify-center overflow-hidden rounded-md';
  const fill = (item: AlbumItem | undefined) => (item?.kind === 'tour' ? 'bg-primary text-primary-foreground' : TINT);
  return (
    <span aria-hidden className="relative h-11 w-14 shrink-0">
      {back && <span className={cn(tile, 'right-0 top-0', fill(back))}>{face(back)}</span>}
      <span className={cn(tile, back ? 'bottom-0 left-0 ring-2 ring-background' : 'inset-0 m-auto', album.kind === 'drawing' ? 'border border-border bg-background' : fill(front))}>{face(front)}</span>
    </span>
  );
}

/** Tours sit in the same strip as photos, a little wider so their name fits. */
function StripTile({ item, active, onClick }: { item: AlbumItem; active: boolean; onClick: () => void }): ReactElement {
  const poster = item.kind === 'tour' ? tourPoster(item.tour) : null;
  return (
    <Button
      type="button"
      variant="ghost"
      aria-pressed={active}
      aria-label={item.kind === 'image' ? item.image.alt : `${item.tour.label}, ${TOUR_KIND[item.embed.kind]}`}
      onClick={onClick}
      className={cn(
        'relative h-14 shrink-0 overflow-hidden rounded-md p-0 hover:bg-transparent md:h-16',
        item.kind === 'image' ? cn('w-20 md:w-24', TINT) : 'w-28 bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground md:w-32',
      )}
    >
      {item.kind === 'image' ? (
        <VibesitesImage {...item.image} fill objectFit="cover" sizes="96px" />
      ) : (
        <>
          {poster && <VibesitesImage {...poster} fill objectFit="cover" sizes="128px" />}
          {poster && <span className="absolute inset-0 bg-[color-mix(in_srgb,var(--vibe-color-primary)_55%,transparent)]" />}
          <span className="relative flex min-w-0 flex-col items-center gap-[2px] px-sm">
            <Play aria-hidden size={12} />
            <span className="max-w-full truncate text-xs font-medium">{item.tour.label}</span>
          </span>
        </>
      )}
      <span
        aria-hidden
        className={cn('pointer-events-none absolute inset-0 rounded-md', active && 'shadow-[inset_0_0_0_2px_var(--vibe-color-text),inset_0_0_0_4px_var(--vibe-color-background)]')}
      />
    </Button>
  );
}

function tourPoster(tour: Tour): Picture | null {
  const url = text(tour.virtual_tour_thumbnail);
  if (!url || !/^https:\/\//i.test(url)) return null;
  const media = tour.virtual_tour_thumbnail_media;
  return { url, alt: tour.label, width: media?.width || undefined, height: media?.height || undefined };
}

/** A home's move-in, price, and Apply. Above the stage on desktop, a sticky footer on phones. */
function HomeBar({ floorplan, home, count }: { floorplan: ReadyPlan; home: ListedUnit; count: string }): ReactElement {
  return (
    <div className="sticky bottom-0 z-10 order-last flex shrink-0 items-center gap-sm border-t border-border bg-background p-md md:static md:order-none md:border-t-0 md:p-0">
      <div className="mr-auto min-w-0">
        <p className="truncate text-xs text-muted-foreground md:text-sm md:font-semibold md:text-foreground">{moveLine(home)}</p>
        <p className="hidden truncate text-xs text-muted-foreground md:block">{`${count} of this home`}</p>
        <p className="font-heading text-2xl text-foreground md:hidden">{home.rentLabel}</p>
      </div>
      <p className="hidden whitespace-nowrap font-heading text-2xl text-foreground md:block">{home.rentLabel}</p>
      {home.unit.apply_url ? (
        <Button asChild>
          <a href={home.unit.apply_url}>{`Apply for ${unitName(home)}`}</a>
        </Button>
      ) : (
        <PlanAction floorplan={floorplan} size="default" />
      )}
    </div>
  );
}

const ZOOMS = [1, 1.5, 2, 3] as const;

/** The drawing on a plain stage. Zoom grows the drawing inside a scroll area, so a visitor pans by scrolling or dragging. */
function ImageStage({ image }: { image: NonNullable<ReturnType<typeof planImage>> }): ReactElement {
  const [step, setStep] = useState(0);
  const zoom = ZOOMS[step];
  const stage = useRef<HTMLDivElement>(null);
  /** The point at the middle of the view, as a fraction of the drawing. Zoom keeps it in the middle. */
  const focus = useRef({ x: 0.5, y: 0.5 });
  const zoomTo = (next: number) => {
    const el = stage.current;
    if (el) {
      focus.current = {
        x: (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth,
        y: (el.scrollTop + el.clientHeight / 2) / el.scrollHeight,
      };
    }
    setStep(next);
  };
  useLayoutEffect(() => {
    const el = stage.current;
    if (!el) return;
    el.scrollLeft = focus.current.x * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = focus.current.y * el.scrollHeight - el.clientHeight / 2;
  }, [step]);
  return (
    <>
      <div ref={stage} className="absolute inset-0 overflow-auto">
        <div className="relative min-h-full min-w-full" style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}>
          <span className="absolute inset-md md:inset-xl">
            <VibesitesImage {...image} fill objectFit="contain" />
          </span>
        </div>
      </div>
      <div className="absolute bottom-md left-1/2 flex -translate-x-1/2 items-center gap-xs rounded-full border border-border bg-background p-xs text-sm text-foreground shadow-md">
        <Button type="button" size="icon" variant="ghost" className="h-10 w-10 rounded-full" disabled={step === 0} onClick={() => zoomTo(step - 1)} aria-label="Zoom out">
          <span aria-hidden className="text-lg leading-none">−</span>
        </Button>
        <span className="w-12 text-center tabular-nums" aria-live="polite">{`${Math.round(zoom * 100)}%`}</span>
        <Button type="button" size="icon" variant="ghost" className="h-10 w-10 rounded-full" disabled={step === ZOOMS.length - 1} onClick={() => zoomTo(step + 1)} aria-label="Zoom in">
          <span aria-hidden className="text-lg leading-none">+</span>
        </Button>
        <Button type="button" variant="ghost" size="sm" className="h-10 rounded-full" disabled={step === 0} onClick={() => zoomTo(0)}>
          Fit
        </Button>
      </div>
    </>
  );
}

/** A poster first, so nothing heavy loads until the visitor asks. Tapping it swaps in the embed. */
function TourStage({ tour, embed }: { tour: Tour; embed: TourEmbed }): ReactElement {
  const [playing, setPlaying] = useState(false);
  if (embed.kind !== 'link' && playing) {
    return (
      <iframe
        src={embed.src}
        title={tour.label}
        className="absolute inset-0 h-full w-full border-0"
        allow="autoplay; fullscreen; xr-spatial-tracking; accelerometer; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }
  const poster = tourPoster(tour);
  return (
    <div className={cn('absolute inset-0 flex flex-col items-center justify-center gap-sm p-lg text-center', TINT)}>
      {poster && (
        <>
          <VibesitesImage {...poster} fill objectFit="cover" />
          <span aria-hidden className="absolute inset-0 bg-[color-mix(in_srgb,var(--vibe-color-background)_70%,transparent)]" />
        </>
      )}
      <div className="relative flex flex-col items-center gap-sm">
        {embed.kind === 'link' ? (
          <>
            <p className={EYEBROW}>Virtual tour</p>
            <p className="font-heading text-2xl text-foreground">{embed.provider}</p>
            <p className="max-w-xs text-sm text-muted-foreground">This tour opens on the provider's site.</p>
            <Button asChild className="mt-sm">
              <a href={tour.href} target="_blank" rel="noreferrer">
                Open tour
                <ExternalLinkIcon aria-hidden size={14} className="ml-xs" />
              </a>
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="ghost" onClick={() => setPlaying(true)} aria-label={`Load ${tour.label}`} className="h-16 w-16 rounded-full bg-primary p-0 text-primary-foreground shadow-md hover:scale-105 hover:bg-primary hover:text-primary-foreground">
              <Play aria-hidden size={22} />
            </Button>
            <p className="mt-sm font-semibold text-foreground">{embed.kind === '3d' ? 'Load 3D tour' : 'Play video'}</p>
            <p className="text-sm text-muted-foreground">{`Loads from ${embed.provider}`}</p>
            <Button asChild variant="link" className={cn(INLINE, 'mt-xs text-sm')}>
              <a href={tour.href} target="_blank" rel="noreferrer">Open in new tab</a>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function Fact({ term, children }: { term: string; children: ReactNode }): ReactElement {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{term}</dt>
      <dd className="font-semibold text-foreground">{children}</dd>
    </div>
  );
}

function unitName(row: ListedUnit): string {
  const name = row.unit.unit_name;
  if (!name) return 'This home';
  return /^\d/.test(name) ? `Unit ${name}` : name;
}

function UnitPicker({
  floorplan,
  today,
  selected,
  select,
}: {
  floorplan: ReadyPlan;
  today: IsoDate;
  selected: ListedUnit | null;
  select: (row: ListedUnit) => void;
}): ReactElement {
  const [open, setOpen] = useState('');
  const first = floorplan.listed.slice(0, UNITS_PREVIEW);
  const rest = floorplan.listed.slice(UNITS_PREVIEW);
  const mediaSlot = floorplan.listed.some((row) => row.photos.length + row.tours.length > 0);
  const option = (row: ListedUnit) => (
    <UnitOption key={row.unit.id ?? row.unit.unit_name ?? row.moveInLabel} row={row} today={today} active={row === selected} mediaSlot={mediaSlot} select={select} />
  );
  return (
    <div>
      <p className={cn('mb-sm', EYEBROW)}>{`Choose a home, ${floorplan.listed.length} available`}</p>
      <ul className="flex flex-col gap-xs">{first.map(option)}</ul>
      {rest.length > 0 && (
        <Accordion type="single" collapsible value={open} onValueChange={setOpen}>
          <AccordionItem value="more">
            <AccordionContent>
              <ul className="flex flex-col gap-xs pt-xs">{rest.map(option)}</ul>
            </AccordionContent>
            <AccordionHeader asChild>
              <div>
                <AccordionTrigger className={MORE}>{open ? 'Show fewer' : `Show all ${floorplan.listed.length}`}</AccordionTrigger>
              </div>
            </AccordionHeader>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
}

function UnitOption({
  row,
  today,
  active,
  mediaSlot,
  select,
}: {
  row: ListedUnit;
  today: IsoDate;
  active: boolean;
  /** Some home in the list has photos or tours, so every row keeps the count's column. */
  mediaSlot: boolean;
  select: (row: ListedUnit) => void;
}): ReactElement {
  const detail = [row.location ?? (row.sqftLabel && `${row.sqftLabel} sq ft`), row.moveIn === 'now' ? 'Move in now' : `Move in ${row.moveInLabel}`]
    .filter(Boolean)
    .join(' · ');
  return (
    <li>
      <Button
        type="button"
        variant="ghost"
        aria-pressed={active}
        onClick={() => select(row)}
        className={cn(
          'h-auto w-full justify-start gap-md whitespace-normal rounded-lg border px-md py-sm text-left text-sm font-normal hover:bg-transparent',
          active ? 'border-primary bg-[color-mix(in_srgb,var(--vibe-color-primary)_6%,transparent)]' : cn('border-border', TINT_HOVER),
        )}
      >
        <span aria-hidden className={cn('h-4 w-4 shrink-0 rounded-full border', active ? 'border-[5px] border-primary' : 'border-border')} />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-foreground">{unitName(row)}</span>
          <span className="block text-xs text-muted-foreground">{detail}</span>
          <Specials offers={row.specials} today={today} className="text-xs" />
        </span>
        {mediaSlot && (
          <span className="flex w-11 shrink-0 items-center gap-xs text-xs tabular-nums text-muted-foreground">
            {row.photos.length + row.tours.length > 0 && (
              <>
                <Camera aria-hidden size={14} />
                <span aria-hidden>{row.photos.length + row.tours.length}</span>
                <span className="sr-only">{mediaCount(row.photos.length, row.tours.length)}</span>
              </>
            )}
          </span>
        )}
        <span className="shrink-0 font-semibold text-foreground">{row.rentLabel}</span>
      </Button>
    </li>
  );
}

/**
 * The plan's amenities, closed to a one-line preview. What the chosen home adds leads, then the plan's, then what
 * only other homes have, each of those linking to its homes.
 */
function AmenityList({ floorplan, selected, select }: { floorplan: ReadyPlan; selected: ListedUnit | null; select: (row: ListedUnit) => void }): ReactElement | null {
  const groups = amenityGroups(floorplan, selected);
  if (!groups.length) return null;
  const total = groups.reduce((sum, group) => sum + group.items.length, 0);
  const home = groups.find((group): group is Extract<AmenityGroup, { kind: 'home' }> => group.kind === 'home');
  const rest = groups.flatMap((group) => (group.kind === 'home' ? [] : group.items.map((item) => item.title)));
  const label = (group: AmenityGroup) => {
    if (group.kind === 'home') return `${unitName(group.home)} adds`;
    if (group.kind === 'plan') return `Every ${floorplan.floorplan_name ?? 'home in this plan'}`;
    return selected ? 'In other homes' : 'In some homes';
  };
  return (
    <section aria-label="Amenities" className="mt-lg border-y border-border">
      <Accordion type="single" collapsible>
        <AccordionItem value="amenities">
          <AccordionHeader>
            <AccordionTrigger className="group flex w-full items-start gap-md py-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-sm">
                  <span className="text-base font-semibold text-foreground">Amenities</span>
                  <span className="text-xs text-muted-foreground">{total}</span>
                </span>
                <span className="mt-xs block truncate text-sm text-muted-foreground group-data-[state=open]:hidden">
                  {home && <span className="font-medium text-foreground">{`${unitName(home.home)} adds ${titleList(home.items.map((item) => item.title), 2)}.`}</span>}
                  {home && rest.length > 0 && ' '}
                  {rest.length > 0 && titleList(rest, 3)}
                </span>
              </span>
              <ChevronDownIcon aria-hidden size={16} className="mt-xs shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180 motion-reduce:transition-none" />
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <div className="flex flex-col gap-lg pb-md">
              {groups.map((group) => {
                const items: readonly (AmenityLine & { readonly homes?: readonly ListedUnit[] })[] = group.items;
                return (
                  <div key={group.kind}>
                    <p className={cn('mb-sm', EYEBROW)}>{label(group)}</p>
                    <ul className="grid grid-cols-1 gap-x-md gap-y-sm sm:grid-cols-2">
                      {items.map((item) => <AmenityItem key={item.title} line={item} homes={item.homes ?? null} select={select} />)}
                    </ul>
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}

/** "Patio, Pool, Grills and 3 more" */
function titleList(titles: readonly string[], shown: number): string {
  const more = titles.length - shown;
  return `${titles.slice(0, shown).join(', ')}${more > 0 ? ` and ${more} more` : ''}`;
}

/** Title, the feed's description under it, and its amount as sent. `homes` links each home that has it. */
function AmenityItem({ line, homes, select }: { line: AmenityLine; homes: readonly ListedUnit[] | null; select: (row: ListedUnit) => void }): ReactElement {
  return (
    <li className="flex gap-sm">
      <span aria-hidden className="flex h-5 shrink-0 items-center">
        <span className="h-[5px] w-[5px] rounded-full bg-muted-foreground" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-foreground">{line.title}</span>
        {line.description && <span className="block text-xs text-muted-foreground">{line.description}</span>}
        {homes && (
          <span className="mt-xs flex flex-wrap gap-x-sm gap-y-xs">
            {homes.map((home) => (
              <Button key={home.unit.id ?? home.unit.unit_name ?? home.moveInLabel} type="button" variant="link" className={cn(INLINE, 'text-xs font-medium')} onClick={() => select(home)}>
                {unitName(home)}
              </Button>
            ))}
          </span>
        )}
      </span>
      {line.amount && <span className="shrink-0 text-right text-sm text-foreground">{line.amount}</span>}
    </li>
  );
}

function bedsHeading(floorplan: ReadyPlan): string {
  if (floorplan.beds == null) return 'Plan';
  if (floorplan.beds === 0) return 'Studio';
  return `${floorplan.beds} ${floorplan.beds === 1 ? 'bed' : 'beds'}`;
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/** "From $1,199/mo" for a range, the single price otherwise, the contact label when unpriced. */
function FromRent({ floorplan }: { floorplan: ReadyPlan }): ReactElement {
  const rent = floorplan.rent;
  if (rent.kind !== 'priced') {
    return <p className="whitespace-nowrap text-sm text-muted-foreground">{rent.label}</p>;
  }
  const ranged = rent.range.min !== rent.range.max;
  return (
    <div className="whitespace-nowrap text-right">
      {ranged && <p className="text-xs text-muted-foreground">From</p>}
      <p className="font-heading text-xl text-foreground md:text-2xl">
        {formatDollars(rent.range.min)}
        <span className="font-sans text-sm text-muted-foreground">/mo</span>
      </p>
    </div>
  );
}

function AvailabilityLabel({ floorplan, chip = false }: { floorplan: ReadyPlan; chip?: boolean }): ReactElement {
  const availability = floorplan.availability;
  const now = (availability.kind === 'listed' && availability.now > 0) || availability.kind === 'reported';
  const soon = availability.kind === 'listed' && availability.now === 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-sm whitespace-nowrap text-sm',
        chip ? 'h-7 rounded-full border border-border bg-background px-sm text-xs font-semibold' : 'font-medium',
        !now && !soon ? 'text-muted-foreground' : 'text-foreground',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-2 w-2 shrink-0 rounded-full',
          now ? 'bg-success' : soon ? 'border-2 border-success' : 'bg-muted-foreground',
        )}
      />
      {availability.label}
    </span>
  );
}

/** The only running special as the feed sends it, or a count when there are several. A long title wraps to two lines, then clips. The dialog lists every special in full. */
const CHIP = 'flex w-fit max-w-full rounded-xl bg-secondary px-sm py-[3px] text-xs font-semibold leading-snug text-secondary-foreground';

/** One line, as sent. When the line cuts the special short, a mouse sees the rest on hover and a finger on tap. A screen reader always gets the whole text. */
function SpecialChip({ offers }: { offers: readonly SpecialOffer[] }): ReactElement {
  const label = (offers.length === 1 && (text(offers[0].title) ?? text(offers[0].description))) || `${offers.length} specials`;
  const line = useRef<HTMLSpanElement>(null);
  const pointer = useRef('');
  const timer = useRef(0);
  const [clipped, setClipped] = useState(false);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const element = line.current;
    if (!element) return;
    const measure = () => setClipped(element.scrollWidth > element.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [label, clipped]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const content = <span ref={line} className="truncate">{label}</span>;
  if (!clipped) return <span className={CHIP}>{content}</span>;
  const hover = (next: boolean) => (event: ReactPointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(next), next ? 150 : 100);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(CHIP, 'relative z-10 cursor-default text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
          onPointerDown={(event) => (pointer.current = event.pointerType)}
          onPointerEnter={hover(true)}
          onPointerLeave={hover(false)}
          onClick={(event) => {
            if (pointer.current === 'mouse') event.preventDefault();
            pointer.current = '';
          }}
        >
          {content}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" onOpenAutoFocus={(event) => event.preventDefault()} className="w-auto max-w-xs p-sm text-sm font-semibold">
        {label}
      </PopoverContent>
    </Popover>
  );
}

function PlanAction({
  floorplan,
  size = 'sm',
  variant,
  label,
  className,
}: {
  floorplan: ReadyPlan;
  size?: 'sm' | 'default' | 'lg';
  variant?: 'link';
  label?: string;
  className?: string;
}): ReactElement | null {
  const action = floorplan.action;
  if (action.kind === 'none') return null;
  return (
    <Button asChild size={size} variant={variant ?? (action.kind === 'apply' ? 'default' : 'outline')} className={className}>
      <a href={action.href} {...tabProps(action.newTab)}>{label && action.kind === 'contact' ? label : action.label}</a>
    </Button>
  );
}

function CostPanel({
  floorplan,
  onTour,
  className,
  children,
}: {
  floorplan: ReadyPlan;
  onTour: (tour: Tour) => void;
  className?: string;
  /** Extra sections after the plan's own, like the row's link to the fee list. */
  children?: ReactNode;
}): ReactElement | null {
  if (!hasCosts(floorplan) && !children) return null;
  const { fees, pets } = floorplan;
  /** A feed that lists the security deposit as a fee already shows it there. */
  const deposit = fees?.some((fee) => /security deposit/i.test(fee.name ?? '')) ? null : floorplan.deposit;
  const range = rentRange(floorplan);
  const tour = panelTour(floorplan);
  return (
    <div className={cn('flex flex-col gap-lg self-start rounded-lg p-lg text-sm', TINT, className)}>
      {(deposit || range) && (
        <dl className="flex flex-col gap-xs">
          {deposit && <Line term="Security deposit">{deposit.replace(/ deposit$/, '')}</Line>}
          {range && <Line term="Rent range">{range}</Line>}
        </dl>
      )}
      {children}
      {pets.length > 0 && <Pets pets={pets} />}
      {tour && (
        <p>
          <Button type="button" variant="link" className={INLINE} onClick={() => onTour(tour)}>
            View virtual tour
          </Button>
        </p>
      )}
    </div>
  );
}

function Line({ term, children }: { term: string; children: ReactNode }): ReactElement {
  return (
    <div className="flex justify-between gap-md">
      <dt className="text-foreground">{term}</dt>
      <dd className="text-right font-semibold text-foreground">{children}</dd>
    </div>
  );
}

function UnitTable({ floorplan, today, onTour }: { floorplan: ReadyPlan; today: IsoDate; onTour: (tour: Tour) => void }): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? floorplan.listed : floorplan.listed.slice(0, UNITS_PREVIEW);
  const columns = floorplan.unitColumns;
  const head = 'py-xs pr-sm font-medium';
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className={cn('border-b border-border', EYEBROW)}>
            <th className="py-xs pr-sm font-medium">Home</th>
            <th className={head}>Move-in</th>
            <th className="py-xs pr-sm text-right font-medium">Rent</th>
            {columns.sqft && <th className={cn(head, 'text-right')}>Sq ft</th>}
            {columns.location && <th className={head}>Location</th>}
            {columns.deposit && <th className={head}>Deposit</th>}
            {columns.special && <th className={cn(head, 'pl-md')}>Special</th>}
            {(columns.tour || columns.apply) && <th className="py-xs font-medium"><span className="sr-only">Links</span></th>}
          </tr>
        </thead>
        <tbody>
          {visible.map((listed) => (
            <UnitLine key={listed.unit.id ?? listed.unit.unit_name ?? listed.moveInLabel} unit={listed} today={today} columns={columns} onTour={onTour} />
          ))}
        </tbody>
      </table>
      {floorplan.listed.length > UNITS_PREVIEW && !expanded && (
        <Button type="button" variant="link" className={cn(MORE, 'h-auto px-0')} onClick={() => setExpanded(true)}>
          {`Show all ${floorplan.listed.length}`}
        </Button>
      )}
    </div>
  );
}

function UnitLine({
  unit,
  today,
  columns,
  onTour,
}: {
  unit: ListedUnit;
  today: IsoDate;
  columns: UnitColumns;
  onTour: (tour: Tour) => void;
}): ReactElement {
  const tour = unit.tour;
  const cell = 'py-sm pr-sm';
  return (
    <tr className="align-middle">
      <td className="py-sm pr-sm">
        <span className="font-semibold text-foreground">{unitName(unit)}</span>
      </td>
      <td className={cell}>{unit.moveInLabel}</td>
      <td className="whitespace-nowrap py-sm pr-sm text-right tabular-nums">{unit.rentLabel}</td>
      {columns.sqft && <td className={cn(cell, 'text-right text-muted-foreground')}>{unit.sqftLabel ?? ''}</td>}
      {columns.location && <td className={cn(cell, 'text-muted-foreground')}>{unit.location ?? ''}</td>}
      {columns.deposit && <td className={cn(cell, 'text-muted-foreground')}>{unit.depositLabel ?? ''}</td>}
      {columns.special && (
        <td className={cn(cell, 'pl-md')}>
          {unit.specials.length > 0 ? (
            <Specials offers={unit.specials} today={today} />
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </td>
      )}
      {(columns.tour || columns.apply) && (
        <td className="whitespace-nowrap py-sm text-right">
          {tour && (
            <Button type="button" variant="link" className={cn(INLINE, 'mr-md')} onClick={() => onTour(tour)}>
              Tour
            </Button>
          )}
          {unit.unit.apply_url && (
            <Button asChild variant="link" className={cn(INLINE, 'font-semibold')}>
              <a href={unit.unit.apply_url}>Apply</a>
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}

const FEE_FROM: Record<FeeSource, string> = { home: 'This home', plan: 'This floor plan', property: 'Community' };
const FEES_PREVIEW = 4;
/** Long enough for the dialog to appear at the top before it glides to the fees, so the homes above register. */
const REVEAL_DELAY = 120;

const ROW_FEES = 6;

/**
 * The required fees every listed home in the plan shares, each as the feed sends it. Fees that differ from home to
 * home stay in the dialog, which lists every fee for one home.
 */
function RowFees({
  floorplan,
  propertyFees,
  fees,
  onOpen,
}: {
  floorplan: ReadyPlan;
  propertyFees: readonly Fee[];
  /** The first home's full list, which the link opens on. */
  fees: FeeList;
  onOpen: () => void;
}): ReactElement {
  const shared = sharedRequiredFees(floorplan, floorplan.listed.map((row) => row.unit), propertyFees);
  const shown = shared.required.slice(0, ROW_FEES);
  return (
    <div className="flex flex-col gap-sm">
      {shown.length > 0 && (
        <>
          <p className={EYEBROW}>Required fees</p>
          <ul className="flex flex-col gap-sm">
            {shown.map((row, index) => (
              <li key={`${row.from}:${row.fee.id ?? index}`} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-md">
                <span className="text-foreground">{row.fee.name}</span>
                <span className="max-w-[8rem] text-right font-semibold text-foreground">{formatFeeAmount(row.fee)}</span>
                {text(row.fee.timing) && <span className="col-span-2 text-xs text-muted-foreground">{text(row.fee.timing)}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      {shared.varies && <p className="text-xs text-muted-foreground">Some homes have other required fees.</p>}
      <p>
        <Button type="button" variant="link" className={INLINE} aria-haspopup="dialog" onClick={onOpen}>
          {`See all ${fees.count} ${fees.count === 1 ? 'fee' : 'fees'}`}
        </Button>
      </p>
    </div>
  );
}

/**
 * The chosen home's fees, its plan's, and the property's, as one list split on `is_required`. Each row keeps the
 * feed's own name, amount, and timing, and says which level it came from. Nothing is merged, deduped, or summed.
 */
function HomeFees({
  fees,
  reveal,
  setReveal,
}: {
  fees: FeeList;
  /** Scroll here and focus the heading. */
  reveal: boolean;
  setReveal: (reveal: boolean) => void;
}): ReactElement | null {
  const [all, setAll] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!reveal || !heading.current) return;
    /** The dialog opens at the top and settles first, so the visitor sees the homes above before it glides down. */
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timer = window.setTimeout(() => {
      heading.current?.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' });
      heading.current?.focus({ preventScroll: true });
      setReveal(false);
    }, still ? 0 : REVEAL_DELAY);
    return () => window.clearTimeout(timer);
  }, [reveal, setReveal]);
  if (!fees.count) return null;
  const optional = all ? fees.optional : fees.optional.slice(0, FEES_PREVIEW);
  const hidden = fees.optional.length - optional.length;
  return (
    <section aria-label="Fees" className="mt-xl text-sm">
      <div className="flex items-baseline justify-between gap-md">
        {/* Clears the sticky title. */}
        <h3 ref={heading} tabIndex={-1} className="scroll-mt-28 text-base font-semibold text-foreground focus:outline-none">Fees</h3>
        <span className="text-xs text-muted-foreground">{`${fees.count} ${fees.count === 1 ? 'fee' : 'fees'}`}</span>
      </div>
      {fees.required.length > 0 && <ListedFeeGroup title="Required" rows={fees.required} />}
      {optional.length > 0 && <ListedFeeGroup title="Optional" rows={optional} />}
      {hidden > 0 && (
        <Button type="button" variant="link" className={cn(MORE, 'h-auto px-0')} onClick={() => setAll(true)}>
          {`Show ${hidden} more optional ${hidden === 1 ? 'fee' : 'fees'}`}
        </Button>
      )}
    </section>
  );
}

function ListedFeeGroup({ title, rows }: { title: string; rows: readonly ListedFee[] }): ReactElement {
  return (
    <div className="mt-md">
      <p className={EYEBROW}>{title}</p>
      <Accordion type="multiple" asChild>
        <ul>
          {rows.map((row, index) => <ListedFeeLine key={`${row.from}:${row.fee.id ?? index}`} value={`${row.from}:${row.fee.id ?? index}`} row={row} />)}
        </ul>
      </Accordion>
    </div>
  );
}

/**
 * Name and amount, then the feed's timing and the level it came from. A fee with a description gets a chevron and
 * opens to it. Fees without one keep the chevron's slot, so amounts line up.
 */
function ListedFeeLine({ value, row }: { value: string; row: ListedFee }): ReactElement {
  const { fee } = row;
  const details = [text(fee.timing), fee.is_refundable === true && 'Refundable', FEE_FROM[row.from]].filter(Boolean).join(' · ');
  const note = feeNote(fee);
  const line = (
    <span className="grid w-full grid-cols-[minmax(0,1fr)_auto_1rem] items-center gap-x-sm py-sm text-left">
      <span className="text-foreground">{fee.name}</span>
      <span className="max-w-[12rem] text-right font-medium text-foreground">{formatFeeAmount(fee)}</span>
      {note ? (
        <ChevronDownIcon aria-hidden size={16} className="text-muted-foreground transition-transform group-data-[state=open]:rotate-180 motion-reduce:transition-none" />
      ) : (
        <span aria-hidden />
      )}
      <span className="col-span-2 text-xs text-muted-foreground">{details}</span>
    </span>
  );
  if (!note) return <li className="border-b border-border last:border-b-0">{line}</li>;
  return (
    <AccordionItem value={value} asChild>
      <li className="border-b border-border last:border-b-0">
        <AccordionHeader asChild>
          <div>
            <AccordionTrigger className="group block w-full rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{line}</AccordionTrigger>
          </div>
        </AccordionHeader>
        <AccordionContent>
          <p className="pb-sm pr-lg text-xs text-muted-foreground">{note}</p>
        </AccordionContent>
      </li>
    </AccordionItem>
  );
}

function Specials({ offers, today, className }: { offers: readonly SpecialOffer[]; today: IsoDate; className?: string }): ReactElement | null {
  const shown = offers.flatMap((offer, index) => {
    const label = formatSpecial(offer, today);
    return label ? [{ key: offer.external_id ?? offer.id ?? index, label, note: specialNote(offer) }] : [];
  });
  if (!shown.length) return null;
  return (
    <ul className={className}>
      {shown.map(({ key, label, note }) => (
        <li key={key}>
          <span className="block font-semibold text-foreground">{label}</span>
          {note && <span className="block font-normal text-muted-foreground">{note}</span>}
        </li>
      ))}
    </ul>
  );
}

/** The description and disclaimer under a titled special, minus any line that repeats the title. */
function specialNote(offer: SpecialOffer): string | null {
  const title = text(offer.title);
  if (!title) return text(offer.disclaimer);
  const lines = [text(offer.description), text(offer.disclaimer)].filter((line): line is string => line != null && line !== title);
  return lines.length ? lines.join(' ') : null;
}

function Terms({ terms }: { terms: NonNullable<ReadyContent['terms']> }): ReactElement {
  return (
    <div className="mt-3xl text-sm">
      <div className="mb-lg flex flex-wrap items-baseline gap-x-md gap-y-xs">
        <h3 className="font-heading text-2xl text-foreground">{terms.fees.length ? 'Fees and policies' : 'Pet policy'}</h3>
        {terms.fees.length > 0 && <p className="text-muted-foreground">These apply to every floor plan.</p>}
      </div>
      <div className="flex flex-col gap-xl">
        {terms.fees.length > 0 && <Fees fees={terms.fees} columns />}
        {terms.pets.length > 0 && (
          <div>
            <p className={cn('mb-sm', EYEBROW)}>Pets</p>
            <Pets pets={terms.pets} />
          </div>
        )}
      </div>
    </div>
  );
}

function Fees({ fees, columns = false }: { fees: readonly Fee[]; columns?: boolean }): ReactElement {
  const required = fees.filter((fee) => fee.is_required === true);
  const optional = fees.filter((fee) => fee.is_required !== true);
  return (
    <div className={cn('flex flex-col', columns ? 'gap-xl' : 'gap-md')}>
      {required.length > 0 && <FeeGroup title="Required fees" fees={required} columns={columns} />}
      {optional.length > 0 && <FeeGroup title="Optional fees" fees={optional} columns={columns} />}
    </div>
  );
}

/** Groups stack. With `columns`, a group's fees flow into two columns on a tablet and three on a desktop, so a long group never leaves a side empty. */
function FeeGroup({ title, fees, columns }: { title: string; fees: readonly Fee[]; columns: boolean }): ReactElement {
  return (
    <div>
      <p className={cn(EYEBROW, 'mb-sm')}>{title}</p>
      <ul className={cn('grid gap-y-md', columns && 'sm:grid-cols-2 sm:gap-x-2xl lg:grid-cols-3')}>
        {fees.map((fee, index) => <FeeLine key={fee.id ?? index} fee={fee} />)}
      </ul>
    </div>
  );
}

/** Name and amount on one line. Timing, refundable, and the feed's description sit under them in small type. */
function FeeLine({ fee }: { fee: Fee }): ReactElement {
  const details = [text(fee.timing), fee.is_refundable === true && 'Refundable'].filter(Boolean).join(' · ');
  const note = feeNote(fee);
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] content-start gap-x-md">
      <span className="text-foreground">{fee.name}</span>
      <span className="max-w-[12rem] text-right font-medium text-foreground">{formatFeeAmount(fee)}</span>
      {details && <span className="col-span-2 text-xs text-muted-foreground">{details}</span>}
      {note && <p className="col-span-2 mt-xs line-clamp-2 text-xs text-muted-foreground" title={note}>{note}</p>}
    </li>
  );
}

/** The feed's own description, unless it only repeats the name. */
function feeNote(fee: Fee): string | null {
  const note = text(fee.description);
  return note && note.toLowerCase() !== text(fee.name)?.toLowerCase() ? note : null;
}

function Pets({ pets }: { pets: ReadyPlan['pets'] }): ReactElement {
  return (
    <ul className="flex flex-col gap-xs text-muted-foreground">
      {pets.map((pet, index) => <li key={pet.id ?? index}>{pet.label}</li>)}
    </ul>
  );
}

function ContactPrompt({ contact }: { contact: Contact }): ReactElement {
  return (
    <div className="mt-2xl flex flex-col gap-md rounded-xl bg-primary p-lg text-primary-foreground md:flex-row md:items-center md:justify-between">
      <div>
        <p className="font-heading text-2xl text-primary-foreground">Not seeing the right home?</p>
        <p className="text-sm text-primary-foreground">Pricing, availability, and specials change often. Ask us about the latest.</p>
      </div>
      <Button asChild variant="secondary" className="shrink-0">
        <a href={contact.href} {...tabProps(contact.newTab)}>{contact.label}</a>
      </Button>
    </div>
  );
}
